import {
  formatMoney,
  productionItemsSchema,
  productionOrderCreateSchema,
  productionOrderUpdateSchema,
  type Page,
  type ProductionOrder,
  type Recipe,
} from "@cafegestor/shared";
import { useQuery } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import {
  Alert,
  Badge,
  Button,
  Card,
  Field,
  Input,
  PageTitle,
  Select,
  Spinner,
  Table,
} from "@/components/ui";
import { ConfirmDialog } from "@/components/ConfirmDialog";
import { KeyFacts, NumberedSection, ProgressSteps } from "@/components/patterns";
import { useCan } from "@/app/me";
import { apiFetch } from "@/lib/api";
import { Pager, SaveError, SearchInput } from "../cadastros/widgets";
import { MovementsTable } from "../estoque/SaldosPage";
import {
  dateBR,
  qty,
  today,
  unitCost,
  unitsOf,
  useDocAction,
  useProductDetail,
  useWarehouses,
} from "../estoque/lib";
import {
  DocStatusBadge,
  LotSelect,
  ProductPicker,
  ReverseAction,
  WarehouseSelect,
} from "../estoque/pickers";

const toInput = (v: string | null | undefined) => (v ? qty(v).replace(/\./g, "") : "");

export function OrdensPage() {
  const can = useCan();
  const [creating, setCreating] = useState(false);
  const [open, setOpen] = useState<string | null>(null);
  const [status, setStatus] = useState("todos");
  const [q, setQ] = useState("");
  const [offset, setOffset] = useState(0);
  const params = new URLSearchParams({ status, limit: "50", offset: String(offset) });
  if (q.trim()) params.set("q", q.trim());
  const list = useQuery({
    queryKey: ["producao", "ordens", params.toString()],
    queryFn: () => apiFetch<Page<ProductionOrder>>(`/producao/ordens?${params.toString()}`),
    placeholderData: (p) => p,
  });

  return (
    <div className="space-y-6">
      <PageTitle
        title="Ordens de produção"
        description="Rascunho → confirmação: o consumo e as perdas saem do estoque, o produto entra com lote. Confirmada, a ordem não volta atrás; correção é por estorno."
        actions={
          can("producao.ordem.lancar") && (
            <Button onClick={() => setCreating(true)} disabled={creating}>
              Nova ordem
            </Button>
          )
        }
      />
      {creating && (
        <NewOrder
          onClose={() => setCreating(false)}
          onCreated={(id) => {
            setCreating(false);
            setOpen(id);
          }}
        />
      )}
      {open && <OrderEditor key={open} id={open} onClose={() => setOpen(null)} />}
      <Card>
        <div className="mb-4 flex flex-wrap gap-3">
          <SearchInput
            placeholder="Buscar número, produto ou lote"
            value={q}
            onChange={(v) => {
              setQ(v);
              setOffset(0);
            }}
          />
          <Select
            className="w-44"
            value={status}
            onChange={(e) => {
              setStatus(e.target.value);
              setOffset(0);
            }}
          >
            <option value="todos">Todas</option>
            <option value="rascunho">Rascunhos</option>
            <option value="confirmado">Confirmadas</option>
            <option value="estornado">Estornadas</option>
            <option value="cancelado">Canceladas</option>
          </Select>
        </div>
        {list.isPending ? (
          <Spinner />
        ) : (
          <>
            <Table
              head={[
                "Número",
                "Data",
                "Produto",
                "Planejado",
                "Produzido",
                "Lote",
                "Rendimento",
                "Situação",
                "",
              ]}
            >
              {list.data?.items.map((o) => (
                <tr key={o.id}>
                  <td className="px-3 py-2 font-mono">{o.number}</td>
                  <td className="px-3 py-2">{dateBR(o.productionDate)}</td>
                  <td className="px-3 py-2">
                    <span className="font-mono text-xs">{o.productCode}</span> {o.productName}
                  </td>
                  <td className="text-right tabular-nums px-3 py-2">
                    {qty(o.plannedQuantity, o.unitCode)}
                  </td>
                  <td className="text-right tabular-nums px-3 py-2">
                    {qty(o.producedQuantity, o.unitCode)}
                  </td>
                  <td className="px-3 py-2 font-mono">{o.outputLotCode ?? o.lotCode ?? "—"}</td>
                  <td className="text-right tabular-nums px-3 py-2">
                    {o.yieldPercent ? `${qty(o.yieldPercent)}%` : "—"}
                  </td>
                  <td className="px-3 py-2">
                    <DocStatusBadge status={o.status} />
                  </td>
                  <td className="px-3 py-2 text-right">
                    <Button size="sm" variant="ghost" onClick={() => setOpen(o.id)}>
                      Abrir
                    </Button>
                  </td>
                </tr>
              ))}
            </Table>
            <Pager total={list.data?.total ?? 0} offset={offset} onOffset={setOffset} />
          </>
        )}
      </Card>
    </div>
  );
}

function useRecipesOf(productId: string) {
  return useQuery({
    queryKey: ["producao", "fichas", "produto", productId],
    queryFn: () => apiFetch<Page<Recipe>>(`/producao/fichas?productId=${productId}&limit=50`),
    enabled: productId !== "",
  });
}

function NewOrder({
  onClose,
  onCreated,
}: {
  onClose: () => void;
  onCreated: (id: string) => void;
}) {
  const whs = useWarehouses();
  const [productId, setProductId] = useState("");
  const [label, setLabel] = useState("");
  const [recipeId, setRecipeId] = useState("");
  const [unitId, setUnitId] = useState("");
  const [planned, setPlanned] = useState("");
  const [date, setDate] = useState(today());
  const [warehouseId, setWarehouseId] = useState("");
  const [lotCode, setLotCode] = useState("");
  const [formError, setFormError] = useState<string | null>(null);
  const product = useProductDetail(productId);
  const recipes = useRecipesOf(productId);
  const units = unitsOf(product.data);
  const wh = warehouseId || whs.data?.find((w) => w.isDefault)?.id || "";
  const create = useDocAction<unknown, ProductionOrder>({
    path: () => "/producao/ordens",
    invalidate: [["producao", "ordens"]],
    onDone: (r) => onCreated(r.id),
  });

  function submit() {
    const parsed = productionOrderCreateSchema.safeParse({
      productId,
      recipeId,
      unitId: unitId || units[0]?.id,
      plannedQuantity: planned,
      outputWarehouseId: wh,
      productionDate: date,
      lotCode,
    });
    if (!parsed.success) return setFormError(parsed.error.issues[0]?.message ?? "Dados inválidos");
    setFormError(null);
    create.mutate(parsed.data);
  }

  return (
    <Card title="Nova ordem de produção">
      <div className="grid gap-4 sm:grid-cols-4">
        <div className="sm:col-span-2">
          <Field label="Produto a fabricar">
            <ProductPicker
              value={productId}
              label={label}
              onChange={(id, l) => {
                setProductId(id);
                setLabel(l);
                setRecipeId("");
                setUnitId("");
              }}
            />
          </Field>
        </div>
        <Field label="Ficha técnica" hint="Vazio: usa a ficha padrão do produto">
          <Select
            value={recipeId}
            onChange={(e) => setRecipeId(e.target.value)}
            disabled={!productId}
          >
            <option value="">Ficha padrão</option>
            {recipes.data?.items
              .filter((r) => r.status === "active")
              .map((r) => (
                <option key={r.id} value={r.id}>
                  {r.name}
                  {r.isDefault ? " (padrão)" : ""}
                </option>
              ))}
          </Select>
        </Field>
        <Field label="Data da produção">
          <Input type="date" value={date} onChange={(e) => setDate(e.target.value)} />
        </Field>
        <Field label="Quantidade planejada">
          <Input inputMode="decimal" value={planned} onChange={(e) => setPlanned(e.target.value)} />
        </Field>
        <Field label="Unidade">
          <Select value={unitId || units[0]?.id || ""} onChange={(e) => setUnitId(e.target.value)}>
            {units.map((u) => (
              <option key={u.id} value={u.id}>
                {u.code}
                {u.factor !== "1" ? ` (${qty(u.factor)} ${product.data?.baseUnitCode})` : ""}
              </option>
            ))}
          </Select>
        </Field>
        <Field label="Depósito (consumo e entrada)">
          <WarehouseSelect value={wh} onChange={setWarehouseId} />
        </Field>
        {product.data?.controlsLot && (
          <Field label="Lote" hint="Vazio: gerado na confirmação (L + data)">
            <Input value={lotCode} onChange={(e) => setLotCode(e.target.value)} />
          </Field>
        )}
      </div>
      <div className="mt-4 flex gap-2">
        <Button onClick={submit} disabled={create.isPending}>
          Criar rascunho
        </Button>
        <Button variant="secondary" onClick={onClose}>
          Cancelar
        </Button>
      </div>
      <div className="mt-3 space-y-2">
        {formError && <Alert tone="error">{formError}</Alert>}
        <SaveError error={create.error} />
      </div>
    </Card>
  );
}

type ItemDraft = {
  key: number;
  kind: "consumo" | "perda";
  productId: string;
  label: string;
  warehouseId: string;
  unitId: string;
  quantity: string;
  lotId: string;
};

function OrderEditor({ id, onClose }: { id: string; onClose: () => void }) {
  const doc = useQuery({
    queryKey: ["producao", "ordens", "detalhe", id],
    queryFn: () => apiFetch<ProductionOrder>(`/producao/ordens/${id}`),
  });
  if (!doc.data) return <Spinner />;
  return <OrderDetail key={doc.data.version} order={doc.data} onClose={onClose} />;
}

function OrderDetail({ order: o, onClose }: { order: ProductionOrder; onClose: () => void }) {
  const can = useCan();
  const draft = o.status === "rascunho";
  const canEdit = draft && can("producao.ordem.lancar");
  const invalidate = [["producao"], ["estoque"]];
  const [items, setItems] = useState<ItemDraft[]>(
    (o.items ?? []).map((i, n) => ({
      key: n + 1,
      kind: i.kind,
      productId: i.productId,
      label: `${i.productCode} — ${i.productName}`,
      warehouseId: i.warehouseId,
      unitId: i.unitId,
      quantity: toInput(i.quantity),
      lotId: i.lotId ?? "",
    })),
  );
  const [dirty, setDirty] = useState(false);
  const [planned, setPlanned] = useState(toInput(o.plannedQuantity));
  const [produced, setProduced] = useState(toInput(o.producedQuantity ?? o.plannedQuantity));
  const [lotCode, setLotCode] = useState(o.lotCode ?? "");
  const [expiresOn, setExpiresOn] = useState(o.expiresOn ?? "");
  const [formError, setFormError] = useState<string | null>(null);
  const [askCancel, setAskCancel] = useState(false);

  const saveHeader = useDocAction<unknown, ProductionOrder>({
    path: () => `/producao/ordens/${o.id}`,
    method: "PUT",
    invalidate,
  });
  const saveItems = useDocAction<unknown, ProductionOrder>({
    path: () => `/producao/ordens/${o.id}/itens`,
    method: "PUT",
    invalidate,
  });
  const recalc = useDocAction<{ version: number }, ProductionOrder>({
    path: () => `/producao/ordens/${o.id}/recalcular`,
    invalidate,
  });
  const confirm = useDocAction<unknown, ProductionOrder>({
    path: () => `/producao/ordens/${o.id}/confirmar`,
    invalidate,
  });
  const cancel = useDocAction<Record<string, never>, ProductionOrder>({
    path: () => `/producao/ordens/${o.id}/cancelar`,
    invalidate,
  });
  const reverse = useDocAction<{ reason: string }, ProductionOrder>({
    path: () => `/producao/ordens/${o.id}/estornar`,
    invalidate,
  });

  function headerBody() {
    return {
      recipeId: o.recipeId,
      unitId: o.unitId,
      plannedQuantity: planned,
      producedQuantity: produced,
      outputWarehouseId: o.warehouseId,
      productionDate: o.productionDate,
      lotCode,
      expiresOn,
      notes: o.notes ?? "",
      version: o.version,
    };
  }
  function submitHeader() {
    const parsed = productionOrderUpdateSchema.safeParse(headerBody());
    if (!parsed.success) return setFormError(parsed.error.issues[0]?.message ?? "Dados inválidos");
    setFormError(null);
    saveHeader.mutate(parsed.data);
  }
  function submitItems() {
    const parsed = productionItemsSchema.safeParse({
      version: o.version,
      items: items.map((i) => ({
        kind: i.kind,
        productId: i.productId,
        warehouseId: i.warehouseId,
        unitId: i.unitId,
        quantity: i.quantity,
        lotId: i.lotId,
      })),
    });
    if (!parsed.success) return setFormError(parsed.error.issues[0]?.message ?? "Dados inválidos");
    setFormError(null);
    saveItems.mutate(parsed.data);
  }

  return (
    <Card
      title={`Ordem ${o.number} — ${o.productName}`}
      actions={
        <Button size="sm" variant="secondary" onClick={onClose}>
          Fechar
        </Button>
      }
    >
      <div className="space-y-5 text-sm">
        <div className="flex flex-wrap items-center gap-3">
          <DocStatusBadge status={o.status} />
          <ProgressSteps
            label="Andamento da ordem"
            steps={[
              "Produto e quantidade planejada",
              "Insumos e ficha técnica",
              "Consumo, perdas e rendimento",
              "Conferência e confirmação",
            ]}
            current={
              o.status === "confirmado"
                ? 4
                : o.status === "rascunho"
                  ? (o.items?.length ?? 0) === 0
                    ? 1
                    : 3
                  : -1
            }
            {...(o.status === "estornado"
              ? { note: "Ordem estornada." }
              : o.status === "cancelado"
                ? { note: "Ordem cancelada." }
                : {})}
          />
        </div>
        <KeyFacts
          items={[
            { label: "Data", value: dateBR(o.productionDate) },
            { label: "Depósito", value: o.warehouseName },
            { label: "Ficha técnica", value: o.recipeName ?? "—" },
            ...(o.outputLotCode ? [{ label: "Lote produzido", value: o.outputLotCode }] : []),
            ...(o.yieldPercent
              ? [
                  {
                    label: "Rendimento",
                    value: `${qty(o.yieldPercent)}% (perda ${qty(String(100 - Number(o.yieldPercent)))}%)`,
                  },
                ]
              : []),
            ...(o.totalCost
              ? [
                  {
                    label: "Custo",
                    value: `${formatMoney(o.totalCost)} (${unitCost(o.unitCost)}/${o.baseUnitCode})`,
                  },
                ]
              : []),
          ]}
        />

        {canEdit && (
          <NumberedSection n={1} title="Produto e quantidade planejada">
            <div className="grid gap-3 sm:grid-cols-5 sm:items-end">
              <Field label={`Planejado (${o.unitCode})`}>
                <Input
                  inputMode="decimal"
                  value={planned}
                  onChange={(e) => setPlanned(e.target.value)}
                />
              </Field>
              {o.controlsLot && (
                <>
                  <Field label="Lote" hint="Vazio: automático">
                    <Input value={lotCode} onChange={(e) => setLotCode(e.target.value)} />
                  </Field>
                  <Field label="Validade" hint="Vazio: data + validade do produto">
                    <Input
                      type="date"
                      value={expiresOn}
                      onChange={(e) => setExpiresOn(e.target.value)}
                    />
                  </Field>
                </>
              )}
              <Button variant="secondary" onClick={submitHeader} disabled={saveHeader.isPending}>
                Salvar dados
              </Button>
            </div>
          </NumberedSection>
        )}

        <NumberedSection
          n={canEdit ? 2 : 1}
          title="Insumos e ficha técnica, consumo e perdas"
          description="Os insumos vêm da ficha técnica; ajuste o consumo e registre as perdas."
        >
          <div className="mb-2 flex flex-wrap items-center justify-end gap-2">
            {canEdit && (
              <div className="flex gap-2">
                {o.recipeId && (
                  <Button
                    size="sm"
                    variant="secondary"
                    disabled={dirty || recalc.isPending}
                    onClick={() => recalc.mutate({ version: o.version })}
                  >
                    Recalcular pela ficha
                  </Button>
                )}
                <Button
                  size="sm"
                  variant="secondary"
                  onClick={() => {
                    setDirty(true);
                    setItems((xs) => [
                      ...xs,
                      {
                        key: Math.max(0, ...xs.map((x) => x.key)) + 1,
                        kind: "perda",
                        productId: "",
                        label: "",
                        warehouseId: o.warehouseId,
                        unitId: "",
                        quantity: "",
                        lotId: "",
                      },
                    ]);
                  }}
                >
                  Adicionar linha
                </Button>
              </div>
            )}
          </div>
          {canEdit ? (
            <div className="space-y-2">
              {items.map((it) => (
                <ItemLine
                  key={it.key}
                  item={it}
                  onChange={(v) => {
                    setDirty(true);
                    setItems((xs) => xs.map((x) => (x.key === v.key ? v : x)));
                  }}
                  onRemove={() => {
                    setDirty(true);
                    setItems((xs) => xs.filter((x) => x.key !== it.key));
                  }}
                />
              ))}
              {dirty && (
                <Button size="sm" onClick={submitItems} disabled={saveItems.isPending}>
                  Salvar consumo
                </Button>
              )}
            </div>
          ) : (
            <Table head={["Tipo", "Produto", "Depósito", "Lote", "Quantidade"]}>
              {o.items?.map((i) => (
                <tr key={i.id}>
                  <td className="px-3 py-2">
                    {i.kind === "perda" ? <Badge tone="warn">perda</Badge> : "consumo"}
                  </td>
                  <td className="px-3 py-2">
                    <span className="font-mono text-xs">{i.productCode}</span> {i.productName}
                  </td>
                  <td className="px-3 py-2">{i.warehouseName}</td>
                  <td className="px-3 py-2 font-mono">
                    {i.lotCode ?? (i.controlsLot ? "FEFO" : "—")}
                  </td>
                  <td className="px-3 py-2 text-right">{qty(i.quantity, i.unitCode)}</td>
                </tr>
              ))}
            </Table>
          )}
        </NumberedSection>

        {formError && <Alert tone="error">{formError}</Alert>}
        <SaveError error={saveHeader.error ?? saveItems.error ?? recalc.error} />

        {draft && can("producao.ordem.confirmar") && (
          <NumberedSection n={3} title="Conferência e confirmação">
            {dirty && <Alert tone="warn">Salve o consumo antes de confirmar.</Alert>}
            <div className="flex flex-wrap items-end gap-3">
              <Field label={`Quantidade produzida (${o.unitCode})`}>
                <Input
                  inputMode="decimal"
                  value={produced}
                  onChange={(e) => setProduced(e.target.value)}
                />
              </Field>
              <Button
                disabled={dirty || confirm.isPending}
                onClick={() => confirm.mutate({ producedQuantity: produced, version: o.version })}
              >
                Confirmar produção
              </Button>
              {can("producao.ordem.lancar") && (
                <Button variant="secondary" onClick={() => setAskCancel(true)}>
                  Cancelar ordem
                </Button>
              )}
              <ConfirmDialog
                open={askCancel}
                title={`Cancelar a ordem ${o.number}?`}
                confirmLabel="Cancelar ordem"
                destructive
                busy={cancel.isPending}
                onCancel={() => setAskCancel(false)}
                onConfirm={() => {
                  setAskCancel(false);
                  cancel.mutate({});
                }}
              >
                A ordem fica cancelada e não poderá ser confirmada. Nada foi produzido nem saiu do
                estoque.
              </ConfirmDialog>
            </div>
            <p className="mt-2 text-xs text-muted-foreground">
              Produto com lote sem lote escolhido: o sistema usa primeiro os lotes que vencem antes
              (FEFO).
            </p>
            <div className="mt-2">
              <SaveError error={confirm.error ?? cancel.error} />
            </div>
          </NumberedSection>
        )}

        {o.status === "estornado" && (
          <Alert tone="warn">Estornada. Motivo: {o.reversalReason}</Alert>
        )}
        {(o.movements?.length ?? 0) > 0 && (
          <div>
            <h3 className="mb-2 font-semibold">Movimentos de estoque</h3>
            <MovementsTable rows={o.movements ?? []} />
          </div>
        )}
        {o.status === "confirmado" && can("producao.ordem.estornar") && (
          <ReverseAction
            pending={reverse.isPending}
            error={reverse.error}
            onConfirm={(reason) => reverse.mutate({ reason })}
          />
        )}
      </div>
    </Card>
  );
}

function ItemLine({
  item,
  onChange,
  onRemove,
}: {
  item: ItemDraft;
  onChange: (v: ItemDraft) => void;
  onRemove: () => void;
}) {
  const product = useProductDetail(item.productId);
  const units = unitsOf(product.data);
  const firstUnit = units[0]?.id;
  useEffect(() => {
    if (!item.unitId && firstUnit) onChange({ ...item, unitId: firstUnit });
  }, [item, firstUnit, onChange]);
  return (
    <div className="grid gap-2 rounded-md border p-2 sm:grid-cols-12 sm:items-end">
      <div className="sm:col-span-1">
        <Field label="Tipo">
          <Select
            value={item.kind}
            onChange={(e) => onChange({ ...item, kind: e.target.value as ItemDraft["kind"] })}
          >
            <option value="consumo">Consumo</option>
            <option value="perda">Perda</option>
          </Select>
        </Field>
      </div>
      <div className="sm:col-span-4">
        <Field label="Produto">
          <ProductPicker
            value={item.productId}
            label={item.label}
            onChange={(id, l) =>
              onChange({ ...item, productId: id, label: l, unitId: "", lotId: "" })
            }
          />
        </Field>
      </div>
      <div className="sm:col-span-2">
        <Field label="Depósito">
          <WarehouseSelect
            value={item.warehouseId}
            onChange={(v) => onChange({ ...item, warehouseId: v, lotId: "" })}
          />
        </Field>
      </div>
      <div className="sm:col-span-2">
        {product.data?.controlsLot ? (
          <Field label="Lote">
            <LotSelect
              productId={item.productId}
              warehouseId={item.warehouseId}
              value={item.lotId}
              emptyLabel="Automático (FEFO)"
              onChange={(v) => onChange({ ...item, lotId: v })}
            />
          </Field>
        ) : (
          <Field label="Lote">
            <Input disabled value="—" />
          </Field>
        )}
      </div>
      <div className="sm:col-span-1">
        <Field label="Qtde">
          <Input
            inputMode="decimal"
            value={item.quantity}
            onChange={(e) => onChange({ ...item, quantity: e.target.value })}
          />
        </Field>
      </div>
      <div className="sm:col-span-1">
        <Field label="Un.">
          <Select
            value={item.unitId}
            onChange={(e) => onChange({ ...item, unitId: e.target.value })}
          >
            {units.map((u) => (
              <option key={u.id} value={u.id}>
                {u.code}
              </option>
            ))}
          </Select>
        </Field>
      </div>
      <div className="sm:col-span-1 text-right">
        <Button size="sm" variant="ghost" onClick={onRemove}>
          Remover
        </Button>
      </div>
    </div>
  );
}
